package org.multiplatform.kotfolio.pages

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.css.FontSize
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.display
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.silk.components.text.SpanText
import org.jetbrains.compose.web.css.DisplayStyle
import org.jetbrains.compose.web.css.em
import org.jetbrains.compose.web.dom.Li
import org.jetbrains.compose.web.dom.Span
import org.multiplatform.kotfolio.components.KotlinCode
import org.multiplatform.kotfolio.layout.BlogLayout
import org.multiplatform.kotfolio.layout.BlogProperties
import org.multiplatform.kotfolio.layout.PageProperties

@Page("metro")
@Composable
fun MetroDependencyInjectionPage() {
    BlogLayout(
        PageProperties(
            "Metro",
            "Metro is a compile-time dependency injection framework that draws heavy inspiration from Dagger, Anvil, and Kotlin-Inject. It seeks to unify their best features under one, cohesive solution while adding a few new features and implemented as a compiler plugin."
        ),
        BlogProperties("Dependency Injection", "Metro")
    ) {
        Column(horizontalAlignment = Alignment.Start)
        {
            SpanText(
                text = "Metro",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Span(Modifier.display(DisplayStyle.Contents).toAttrs())
            {
                SpanText(
                    text = "Metro is a compile-time dependency injection framework. mplemented as a Kotlin compiler plugin (using FIR/IR), it does not require KAPT or KSP, which significantly improves build performance. : Includes features like @ContributesTo and @ContributesBinding, allowing developers to contribute dependencies from different modules without manual wiring.",
                    modifier = Modifier.padding(topBottom = 0.3.em)
                        .fontSize(FontSize.Medium)
                )
            }

            SpanText(
                text = "MetroX ViewModel Compose",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Span(Modifier.display(DisplayStyle.Contents).toAttrs())
            {
                SpanText(
                    text = "Compose integration for MetroX ViewModel. This artifact provides Compose-specific utilities for injecting ViewModels",
                    modifier = Modifier.padding(topBottom = 0.3.em)
                        .fontSize(FontSize.Medium)
                )
            }

            KotlinCode(
                code = """
                    plugins {
                      id("dev.zacsweers.metro") version ""
                    }
                    
                    implementation("dev.zacsweers.metro:metrox-viewmodel-compose:")
                    
                    """.trimIndent()
            )

            SpanText(
                text = "DependencyGraph",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                        .fontWeight(FontWeight.Bold)
            )

            Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Starting point and definition of how dependencies are initialized and injected into services needed throughout your app.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

            SpanText(
                    text = "Inject",
                    modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                        .fontWeight(FontWeight.Bold)
                )

            Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Create this class/object automatically inject notify by metro.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

            //https://github.com/Team-Bigong/oguri-kotlin-multiplatform.git
            KotlinCode(
                code = """
                        //ViewModel
                        @Inject
                        @OptIn(ExperimentalCoroutinesApi::class)
                        class CalendarViewModel() : ViewModel() { }
                        
                        @DependencyGraph
                        interface AppGraph {
                            val calendarViewModelProvider: Provider<CalendarViewModel>
                        }   
                        
                        //usecase
                        @Inject
                        class GetCalendarRecommendationUseCase() {}
                        
                        //UI ViewModel Paramter
                        val homeViewModelLazy =
                            remember(authSessionVersion) {
                                lazy(LazyThreadSafetyMode.NONE) {
                                    appGraph.homeViewModelProvider()
                                }
                            }
                    """.trimIndent()
            )

            SpanText(
                    text = "Provides",
                    modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                        .fontWeight(FontWeight.Bold)
            )

            Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Provide actual implementations per platform.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

            KotlinCode(
                code = """
                        //Repository 
                        @Provides
                        fun provideCalendarRepository(implementation: DefaultCalendarRepository): CalendarRepository = implementation

                        // commonMain
                        expect class DeviceInfo {
                            fun getName(): String
                        }
                        
                        @Provides
                        fun provideDeviceInfo(): DeviceInfo = DeviceInfo() // unnecessary use to @ContributesBinding
                        
                        @Inject
                        class AppService(
                            private val deviceInfo: DeviceInfo
                        ) {
                            fun printDevice() {
                                println(deviceInfo.getName())
                            }
                        }
                    """.trimIndent()
            )

            SpanText(
                    text = "Qualifier",
                    modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                        .fontWeight(FontWeight.Bold)
            )

            SpanText(
                    text = "AssistedInject",
                    modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                        .fontWeight(FontWeight.Bold)
            )

            Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Assisted injection (runtime parameters + injected dependencies + Repository)",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

            SpanText(
                text = "BindingContainer",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            //https://github.com/gcaguilar/biciradar.git
            KotlinCode(
                code = """
                    @BindingContainer
                    object CoreBindings {
                      @SingleIn(AppScope::class)
                      @Provides
                      fun provideAppScope(): CoroutineScope = CoroutineScope(SupervisorJob() + Dispatchers.Default)

                      @SingleIn(AppScope::class)
                      @Provides
                      fun provideJson(): Json =
                        Json {
                          ignoreUnknownKeys = true
                          explicitNulls = false
                        }
                    }
                    
                    @DependencyGraph(
                      AppScope::class,
                      bindingContainers = [CoreBindings::class, DatabaseBindings::class, NetworkBindings::class, GeoBindings::class],
                    )
                    internal interface MobileGraph :
                      SharedGraph,
                      ViewModelGraph {
                      @DependencyGraph.Factory
                      fun interface Factory {
                        fun create(
                          @Includes platformBindings: PlatformBindings,
                        ): MobileGraph
                      }
                    }
                    
                    @BindingContainer
                    object NetworkBindings {
                      @SingleIn(AppScope::class)
                      @Provides
                      fun provideHttpClient(
                        httpClientFactory: BiziHttpClientFactory,
                        json: Json,
                      ): HttpClient = httpClientFactory.create(json)

                      @SingleIn(AppScope::class)
                      @Provides
                      fun provideDatosBiziApi(httpClient: HttpClient): DatosBiziApi = DatosBiziApiImpl(httpClient)
                    }
                    
                    interface DatosBiziApi {
                      suspend fun fetchPatterns(stationId: String): List<StationHourlyPattern>
                    }

                    class DatosBiziApiImpl(
                      private val httpClient: HttpClient,
                    ) : DatosBiziApi {
                      override suspend fun fetchPatterns(stationId: String): List<StationHourlyPattern> =
                        httpClient.get("").body()
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "GraphExtension",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            //https://github.com/gcaguilar/biciradar.git
            KotlinCode(
                code = """
                    @Scope
                    annotation class OnboardingScope
                    
                    @GraphExtension(OnboardingScope::class)
                    interface OnboardingGraph {
                      @ContributesTo(AppScope::class)
                      @GraphExtension.Factory
                      interface Factory {
                        fun createOnboardingGraph(): OnboardingGraph
                      }
                    }

                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    @DependencyGraph
                    interface AppGraph {
                        @DependencyGraph.Factory
                        fun interface Factory {
                            fun create(
                                @Provides httpClient: HttpClient,
                            ): AppGraph
                        }
                    }
                    
                    //View
                    val appGraphFactory = createGraphFactory<AppGraph.Factory>()
                    val httpClient = provideHttpClient()
                    appGraphFactory.create(
                        httpClient = httpClient
                    )
                    
                    fun provideHttpClient(): HttpClient =
                        HttpClient(engineFactory = providePlatformHttpClientEngineFactory()) {
                            expectSuccess = true
                            install(plugin = OguriRequestHeadersPlugin)
                            install(plugin = ContentNegotiation) {
                                json(
                                    json =
                                        Json {
                                            ignoreUnknownKeys = true
                                        },
                                )
                            }
                        }
                        
                    expect fun providePlatformHttpClientEngineFactory(): HttpClientEngineFactory<*>    
                    """.trimIndent()
            )

            //https://github.com/herrbert74/Purecipes
            KotlinCode(
                code = """
                    import dev.zacsweers.metro.Qualifier

                    /**
                     * These classes are provided in the base.android module to keep this module free from Hilt and Android dependencies.
                     */

                    @Qualifier
                    @Retention(AnnotationRetention.BINARY)
                    annotation class IoDispatcher

                    @Qualifier
                    @Retention(AnnotationRetention.BINARY)
                    annotation class MainDispatcher

                    @Qualifier
                    @Retention(AnnotationRetention.BINARY)
                    annotation class DefaultDispatcher
                    
                    import dev.zacsweers.metro.AppScope
                    import dev.zacsweers.metro.ContributesTo
                    import dev.zacsweers.metro.Provides
                    import kotlinx.coroutines.CoroutineDispatcher
                    import kotlinx.coroutines.Dispatchers

                    @ContributesTo(AppScope::class)
                    interface SingletonModule {

                    	@DefaultDispatcher
                    	@Provides
                    	fun providesDefaultDispatcher(): CoroutineDispatcher = Dispatchers.Default

                    	@IoDispatcher
                    	@Provides
                    	fun providesIoDispatcher(): CoroutineDispatcher = ioDispatcher()

                    	@MainDispatcher
                    	@Provides
                    	fun providesMainDispatcher(): CoroutineDispatcher = Dispatchers.Main
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "Graph",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            //https://github.com/kropp/HouseHelper.git
            KotlinCode(
                code = """
                    @DependencyGraph(AppScope::class)
                    interface AppGraph : ViewModelGraph {
                        @Provides
                        @SingleIn(AppScope::class)
                        fun provideMetroViewModelFactory(
                            viewModelProviders: Map<KClass<out ViewModel>, Provider<ViewModel>>,
                            manualAssistedFactoryProviders: Map<KClass<out ManualViewModelAssistedFactory>, Provider<ManualViewModelAssistedFactory>>,
                        ): MetroViewModelFactory = object : MetroViewModelFactory() {
                            override val viewModelProviders get() = viewModelProviders
                            override val manualAssistedFactoryProviders get() = manualAssistedFactoryProviders
                        }
                    }
                    
                    //androidMain
                    class MyApplication : Application() {
                        val appGraph: AppGraph by lazy {
                            createGraph<AppGraph>()
                        }
                    }
                    
                    //iosMain
                    private val appGraph: AppGraph by lazy { createGraph<AppGraph>() }

                    fun MainViewController() = ComposeUIViewController { App(appGraph) }
                    
                    @Composable
                    fun App(appGraph: AppGraph) {
                        // TODO Task 14: customize theme
                        MaterialTheme {
                            CompositionLocalProvider(LocalMetroViewModelFactory provides appGraph.metroViewModelFactory) {
                            }
                        }
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "ViewModel",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            //https://github.com/kropp/HouseHelper.git
            KotlinCode(
                code = """
                    @SingleIn(AppScope::class)
                    @ContributesBinding(AppScope::class)
                    class DemoHouseService : HouseService { ]
                    
                    interface HouseService { }
                    
                    //1 Parameter
                    @AssistedInject
                    class RoomViewModel(
                        private val houseService: HouseService,
                        @Assisted private val roomId: RoomId,
                    ) : ViewModel() {
                    
                        @AssistedFactory
                        @ManualViewModelAssistedFactoryKey(Factory::class)
                        @ContributesIntoMap(AppScope::class)
                        fun interface Factory : ManualViewModelAssistedFactory {
                            fun create(roomId: RoomId): RoomViewModel
                        }
                    }
                    
                    val roomViewModel: RoomViewModel = viewModel(key = room.id.value) {
                      RoomViewModel(DemoHouseService(), room.id)
                    }
                    
                    //2 Parameter
                    @AssistedInject
                    class SpeakerDetailViewModel(
                        private val service: ConferenceService,
                        @Assisted speakerId: SpeakerId,
                    ) : ViewModel() { 
                        @AssistedFactory
                        @ManualViewModelAssistedFactoryKey(Factory::class)
                        @ContributesIntoMap(AppScope::class)
                        fun interface Factory : ManualViewModelAssistedFactory {
                            fun create(speakerId: SpeakerId): SpeakerDetailViewModel
                        }
                    }
                    
                    @Composable
                    fun SpeakerDetailScreen(
                        speakerId: SpeakerId,
                        onBack: () -> Unit,
                        onSession: (SessionId) -> Unit,
                        viewModel: SpeakerDetailViewModel =
                            assistedMetroViewModel<SpeakerDetailViewModel, SpeakerDetailViewModel.Factory> {
                                create(speakerId)
                            }
                    ) { }
                    
                    //3 No parameter
                    @ContributesIntoMap(AppScope::class)
                    @ViewModelKey(DashboardViewModel::class)
                    class DashboardViewModel() : ViewModel() {
                    }

                    @Composable
                    fun DashboardScreen(
                        viewModel: DashboardViewModel = metroViewModel(),
                    ) { }
                    
                    //4
                    @OptIn(ExperimentalCoroutinesApi::class)
                    @Inject
                    @SingleIn(AppScope::class)
                    class ConferenceService() {}
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                       @Provides
                       @SingleIn(AppScope::class)
                       fun provideAppScope(): CoroutineScope = CoroutineScope(SupervisorJob() + Dispatchers.Default)
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    interface AppGraph : ViewModelGraph {
                        val logger: Logger
                        val scope: CoroutineScope
    
                        @Provides
                        @SingleIn(AppScope::class)
                        fun provideMetroViewModelFactory(
                            viewModelProviders: Map<KClass<out ViewModel>, Provider<ViewModel>>,
                            manualAssistedFactoryProviders: Map<KClass<out ManualViewModelAssistedFactory>, Provider<ManualViewModelAssistedFactory>>,
                        ): MetroViewModelFactory = object : MetroViewModelFactory() {
                            override val viewModelProviders get() = viewModelProviders
                            override val manualAssistedFactoryProviders get() = manualAssistedFactoryProviders
                        }
                    
                        @Provides
                        @SingleIn(AppScope::class)
                        fun provideHttpClient(
                            applicationStorage: ApplicationStorage,
                            platformFlags: Flags,
                            logger: Logger,
                        ): HttpClient {

                            val flags = applicationStorage.getFlagsBlocking()
                            val baseUrl = when {
                                flags != null && (flags != platformFlags) -> URLs.STAGING_URL
                                else -> URLs.PRODUCTION_URL
                            }

                            return HttpClient {
                                install(ContentNegotiation) {
                                    json()
                                }

                                install(Logging) {
                                    level = LogLevel.HEADERS
                                    this.logger = object : KtorLogger {
                                        override fun log(message: String) {
                                            logger.log("HttpClient") { message }
                                        }
                                    }
                                }

                                expectSuccess = true
                                install(HttpTimeout) {
                                    requestTimeoutMillis = 5000
                                }

                                install(HttpRequestRetry) {
                                    retryOnServerErrors(maxRetries = 3)
                                    exponentialDelay()
                                }

                                install(DefaultRequest) {
                                    url.takeFrom(baseUrl)
                                }
                            }
                        }
                    }
                    
                    //androidMain
                    interface BaseAndroidAppGraph : AppGraph, MetroAppComponentProviders {

                        @Provides
                        @SingleIn(AppScope::class)
                        fun provideApplicationContext(application: Application): Context = application
                    }
                    
                    @DependencyGraph(AppScope::class)
                    interface AndroidAppGraph : BaseAndroidAppGraph {
                        @DependencyGraph.Factory
                        interface Factory {
                            fun create(
                                @Provides application: Application,
                                @Provides @NotificationIcon iconRes: Int,
                                @Provides platformFlags: Flags = Flags(),
                            ): AndroidAppGraph
                        }
                    }
                    
                    class KotlinConfApplication : Application(), MetroApplication {

                        private val appGraph: AndroidAppGraph by lazy {
                            createGraphFactory<AndroidAppGraph.Factory>().create(
                                application = this,
                                iconRes = R.drawable.app_icon,
                            )
                        }

                        override val appComponentProviders: MetroAppComponentProviders
                            get() = appGraph

                        override fun onCreate() {
                            super.onCreate()

                            initApp(
                                appGraph = appGraph,
                                platformLogger = AndroidLogger(),
                            )
                        }
                    }
                    
                    //iosMain
                    @DependencyGraph(AppScope::class)
                    interface IosAppGraph : AppGraph { }
                    """.trimIndent()
            )

            //https://github.com/smithjustinn84-netizen/waterBuddy.git (Test Cases)
            KotlinCode(
                code = """
                    /**
                     * A [androidx.lifecycle.ViewModelProvider.Factory] that uses an injected map of [KClass] to [Provider] of [ViewModel]
                     * to create ViewModels.
                     */
                    @ContributesBinding(AppScope::class)
                    @Inject
                    class InjectedViewModelFactory(
                        override val viewModelProviders: Map<KClass<out ViewModel>, Provider<ViewModel>>,
                        override val assistedFactoryProviders: Map<KClass<out ViewModel>, Provider<ViewModelAssistedFactory>>,
                        override val manualAssistedFactoryProviders: Map<KClass<out ManualViewModelAssistedFactory>, Provider<ManualViewModelAssistedFactory>>,
                    ) : MetroViewModelFactory()
                    
                    ----- domain -----
                    //usecase
                    @SingleIn(AppScope::class)
                    @Inject
                    class UpdateWaterIntakeUseCase(
                        private val repository: WaterRepository,
                    ) { }
                    
                    //repository
                    interface WaterRepository { }
                    
                    ----- data -----
                    //repository
                    @OptIn(ExperimentalTime::class)
                    @SingleIn(AppScope::class)
                    @ContributesBinding(AppScope::class)
                    @Inject
                    class WaterRepositoryImpl() : WaterRepository { }
                    
                    @Composable
                    fun App(component: AppGraph) {
                        CompositionLocalProvider(LocalMetroViewModelFactory provides component.metroViewModelFactory) {
                        }
                    }
                    """.trimIndent()
            )
        }
    }
}

//https://github.com/kropp/HouseHelper.git
//https://github.com/JetBrains/kotlinconf-app.git
//https://code.cash.app/cash-android-moves-to-metro
//https://github.com/smithjustinn84-netizen/waterBuddy.git (Test Case kotest, Metro DI)
//https://github.com/gcaguilar/bizimobile.git
//https://www.youtube.com/watch?v=jVfmtVKa604
//https://github.com/Team-Bigong/oguri-kotlin-multiplatform.git
//https://github.com/gcaguilar/biciradar.git (BindingContainer, ContributesTo,  ContributesBinding, GraphExtension, Google Map)
//https://github.com/Team-Bigong/oguri-kotlin-multiplatform.git (Metro DI, OnBoarding AnimatedContext good, calendar, apple & google login, Spring backed, react admin panel)
//https://github.com/WAHID-QANDIL/NewsCMP
